import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, Globe, HardDrive, History, Swords, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DiscordIcon from "@/components/DiscordIcon";
import { discordLogin } from "@/components/Navbar";
import { newDraft } from "@/lib/draft";
import { forgetDraft, listDrafts, rememberDraft, save } from "@/lib/drafterData";

const MODES = [{ key: "tournament", label: "Tournoi" }, { key: "fearless", label: "Fearless" }];
const SIDE_CHOICES = [
  { key: "blue", label: "Side bleu", cls: "text-sky-400" },
  { key: "red", label: "Side rouge", cls: "text-red-500" },
  { key: "spectator", label: "Spectateur", cls: "text-slate-300" },
];
const WHERE = [
  { key: "online", label: "En ligne", hint: "Un lien par capitaine et un lien spectateur, gardés indéfiniment.", Icon: Globe },
  { key: "local", label: "Local", hint: "Tout se joue sur cet appareil, même sans connexion.", Icon: HardDrive },
];

export const modeLabel = (mode) => MODES.find((m) => m.key === mode)?.label;

export const draftLink = (entry, side = entry.side) =>
  entry.kind === "local" ? `/drafter/local/${entry.id}` : `/drafter/${entry.id}${entry.tokens?.[side] ? `#t=${entry.tokens[side]}` : ""}`;

// Deux clics pour supprimer : le premier arme le bouton, le second confirme.
function DeleteButton({ onConfirm }) {
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!armed) return undefined;
    const t = setTimeout(() => setArmed(false), 4000);
    return () => clearTimeout(t);
  }, [armed]);
  const click = async () => {
    if (!armed) return setArmed(true);
    setBusy(true);
    await onConfirm();
    setBusy(false);
    setArmed(false);
  };
  return (
    <button type="button" onClick={click} disabled={busy} data-testid="drafter-delete" aria-label={armed ? "Confirmer la suppression" : "Supprimer la draft"}
      className={`flex shrink-0 items-center gap-1.5 rounded-xl border px-3 text-sm transition-colors disabled:opacity-50 ${
        armed ? "border-red-500 bg-red-500/10 text-red-400" : "border-white/10 text-slate-500 hover:border-red-500/60 hover:text-red-400"}`}>
      <Trash2 className="h-4 w-4" /> {armed ? "Confirmer" : <span className="sr-only">Supprimer</span>}
    </button>
  );
}

function Segmented({ label, options, value, onChange, testid }) {
  return (
    <fieldset>
      <legend className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{label}</legend>
      <div className="mt-2 flex flex-wrap gap-1 rounded-xl border border-white/10 p-1" data-testid={testid}>
        {options.map((o) => (
          <button key={o.key} type="button" aria-pressed={value === o.key} onClick={() => onChange(o.key)}
            className={`min-w-[2.75rem] flex-1 rounded-lg px-3 py-2 font-heading text-sm font-bold uppercase transition-colors ${
              value === o.key ? "bg-orange-500 text-[#060B18]" : `${o.cls || "text-slate-400"} hover:bg-white/5`
            }`}>
            {o.label}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

export default function Drafter() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ blue: "", red: "", mode: "tournament", games: 1, timer: true, side: "blue", where: "online" });
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();
  const [accountDrafts, setAccountDrafts] = useState([]);
  const [, refresh] = useState(0);
  useEffect(() => {
    if (user) api.get("/drafts/mine").then((r) => setAccountDrafts(r.data)).catch(() => {});
  }, [user]);
  // Drafts du compte (tous appareils) et de cet appareil ; l'entrée locale garde le side choisi ici
  const local = listDrafts();
  const history = [...local, ...accountDrafts.filter((a) => !local.some((e) => e.id === a.id))]
    .sort((a, b) => b.created.localeCompare(a.created));
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const remove = async (entry) => {
    if (entry.kind === "online") {
      try {
        await api.post(`/drafts/${entry.id}/delete`, { blue: entry.tokens?.blue || "", red: entry.tokens?.red || "" });
      } catch (err) {
        // Déjà supprimée ailleurs : on la retire aussi d'ici
        if (err?.response?.status !== 404) return toast.error(errMsg(err));
      }
    }
    forgetDraft(entry.id);
    setAccountDrafts((list) => list.filter((a) => a.id !== entry.id));
    refresh((n) => n + 1);
    toast.success("Draft supprimée");
  };

  const create = async (e) => {
    e.preventDefault();
    const settings = { blue: form.blue.trim(), red: form.red.trim(), mode: form.mode, games: form.games, timer: form.timer };
    const base = { mode: form.mode, games: form.games, names: newDraft(settings).names, created: new Date().toISOString() };
    if (form.where === "local") {
      const entry = { ...base, id: Math.random().toString(36).slice(2, 10), kind: "local" };
      save(`drafter:local:${entry.id}`, newDraft(settings));
      rememberDraft(entry);
      return navigate(draftLink(entry));
    }
    setBusy(true);
    try {
      const { data } = await api.post("/drafts", settings);
      const entry = { ...base, id: data.id, kind: "online", tokens: data.tokens, side: form.side };
      rememberDraft(entry);
      navigate(draftLink(entry));
    } catch (err) {
      toast.error(errMsg(err));
      setBusy(false);
    }
  };

  return (
    <div data-testid="drafter-page" className="page-enter mx-auto max-w-5xl px-4 py-16 sm:px-8 lg:py-24">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500">Outil</p>
      <h1 className="mt-2 text-2xl font-extrabold uppercase tracking-tight sm:text-3xl lg:text-4xl">Drafter League of Legends</h1>
      <p className="mt-3 text-slate-400">Draft de compétition : ordre officiel des bans et des picks, timer, Fearless et séries jusqu'à 7 games.</p>

      <form onSubmit={create} className="card-tactical mt-8 grid gap-6 p-5 sm:p-8">
        <div className="grid gap-4 sm:grid-cols-2">
          {[["blue", "Équipe bleue", "text-sky-400"], ["red", "Équipe rouge", "text-red-500"]].map(([side, ph, cls]) => (
            <label key={side} className="block">
              <span className={`text-xs font-bold uppercase tracking-[0.2em] ${cls}`}>{ph}</span>
              <input value={form[side]} onChange={(e) => set({ [side]: e.target.value })} maxLength={30} placeholder={ph}
                data-testid={`drafter-name-${side}`}
                className="mt-2 w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 outline-none placeholder:text-slate-600 focus:border-orange-500" />
            </label>
          ))}
        </div>

        <div className="grid gap-6 sm:grid-cols-2">
          <Segmented label="Mode de jeu" options={MODES} value={form.mode} onChange={(mode) => set({ mode })} testid="drafter-setup-mode" />
          <Segmented label="Nombre de games" options={[1, 2, 3, 4, 5, 6, 7].map((n) => ({ key: n, label: String(n) }))}
            value={form.games} onChange={(games) => set({ games })} testid="drafter-setup-games" />
        </div>

        <div className="grid gap-6 sm:grid-cols-2">
          <fieldset>
            <legend className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Où se joue la draft</legend>
            <div className="mt-2 grid gap-2">
              {WHERE.map(({ key, label, hint, Icon }) => (
                <button key={key} type="button" aria-pressed={form.where === key} onClick={() => set({ where: key })} data-testid={`drafter-where-${key}`}
                  className={`flex items-start gap-3 rounded-xl border p-3 text-left transition-colors ${form.where === key ? "border-orange-500 bg-orange-500/10" : "border-white/10 hover:border-white/20"}`}>
                  <Icon className="mt-0.5 h-5 w-5 shrink-0 text-orange-500" />
                  <span>
                    <span className="block font-heading text-sm font-bold uppercase">{label}</span>
                    <span className="block text-sm text-slate-400">{hint}</span>
                  </span>
                </button>
              ))}
            </div>
          </fieldset>
          <div className="grid content-start gap-6">
            {form.where === "online" && (
              <Segmented label="Votre side" options={SIDE_CHOICES} value={form.side} onChange={(side) => set({ side })} testid="drafter-setup-side" />
            )}
            <label className="flex cursor-pointer items-center gap-3 text-sm text-slate-300">
              <input type="checkbox" checked={form.timer} onChange={(e) => set({ timer: e.target.checked })} className="h-4 w-4 accent-orange-500" data-testid="drafter-setup-timer" />
              Timer de 35 secondes par ban et par pick
            </label>
          </div>
        </div>

        <button type="submit" disabled={busy} data-testid="drafter-create"
          className="flex items-center justify-center gap-2 rounded-xl bg-orange-500 px-6 py-3 font-heading text-lg font-bold uppercase tracking-wide text-[#060B18] transition-all hover:scale-[1.01] hover:bg-orange-400 disabled:opacity-60">
          <Swords className="h-5 w-5" /> {busy ? "Création…" : "Créer la draft"}
        </button>
      </form>

      {user === false && (
        <div className="card-tactical mt-6 flex flex-wrap items-center justify-between gap-3 p-4" data-testid="drafter-login-hint">
          <p className="text-sm text-slate-300">Connectez-vous pour retrouver vos drafts sur tous vos appareils.</p>
          <button onClick={discordLogin} data-testid="drafter-login-discord"
            className="flex items-center gap-2 rounded-xl bg-[#5865F2] px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-[#4752c4]">
            <DiscordIcon className="h-4 w-4" /> Continuer avec Discord
          </button>
        </div>
      )}

      {history.length > 0 && (
        <section className="mt-12" data-testid="drafter-history">
          <h2 className="flex items-center gap-2 font-heading text-xl font-bold uppercase"><History className="h-5 w-5 text-orange-500" /> Vos drafts</h2>
          <p className="mt-1 text-sm text-slate-500">
            {user ? "Sur cet appareil et sur votre compte." : "Enregistrées sur cet appareil."} Les liens en ligne restent valables indéfiniment.
          </p>
          <ul className="mt-4 grid gap-2">
            {history.map((e) => (
              <li key={e.id} className="flex items-stretch gap-2">
                <Link to={draftLink(e)} onClick={() => rememberDraft(e)} className="card-tactical flex min-w-0 flex-1 flex-wrap items-center gap-x-4 gap-y-1 p-4 transition-colors hover:border-orange-500/50">
                  <span className="font-heading font-bold uppercase">
                    <span className="text-sky-400">{e.names.blue}</span> <span className="text-slate-500">vs</span> <span className="text-red-500">{e.names.red}</span>
                  </span>
                  <span className="text-sm text-slate-400">{modeLabel(e.mode)} · {e.games} game{e.games > 1 ? "s" : ""}</span>
                  <span className="ml-auto flex items-center gap-1.5 text-xs uppercase tracking-wider text-slate-500">
                    {e.kind === "local" ? <HardDrive className="h-3.5 w-3.5" /> : e.side === "spectator" ? <Eye className="h-3.5 w-3.5" /> : <Globe className="h-3.5 w-3.5" />}
                    {e.kind === "local" ? "Local" : "En ligne"} · {new Date(e.created).toLocaleDateString("fr-FR")}
                  </span>
                </Link>
                <DeleteButton onConfirm={() => remove(e)} />
              </li>
            ))}
          </ul>
        </section>
      )}

      <p className="mt-12 text-xs text-slate-600">
        Emisphere Esport n'est pas affilié à Riot Games. League of Legends et les visuels des champions sont la propriété de Riot Games, Inc.
      </p>
    </div>
  );
}
